import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { StackActions, useNavigation } from '@react-navigation/native';
import ReCAPTCHA from 'react-google-recaptcha';
import { HelperText, Text } from 'react-native-paper';
import { isNonEmptyString } from 'ramda-adjunct';

import {
  GuestRegistration,
  Header40,
  HtmlView,
  OneTimePasswordVerification,
  useManagedState,
  ValidatedComponent,
} from '@luminate/luminate-native-components';

import { GuestRegistrationRequest, GuestService, Optional, PhoneType } from '@luminate/luminate-ts-sdk';

import { useAuth } from '../../contexts';
import { Button, Dialog } from '../common';
import { SCREEN_NAMES } from '../routes';
import Environment from '../../models/Environment';
import { DeviceType, useResponsive } from '../hooks';

const OTP_ERROR_MESSAGES = {
  INVALID: 'Invalid verification code.',
  EXCEEDED: 'Too many OTP attempts. Please try again after time has expired.',
  ERROR: `An error occurred. Please try again later. If the problem persists, please contact support.`,
};

export const GuestCheckoutScreen = () => {
  const { deviceType } = useResponsive();
  const { genders, registerGuest, cart, session } = useAuth();
  const navigation = useNavigation();
  const captchaRef = useRef<ReCAPTCHA>(null);

  // Validated fields
  const [firstName, onFirstNameChanged] = useManagedState<string>('');
  const [middleName, onMiddleNameChanged] = useManagedState<Optional<string>>();
  const [lastName, onLastNameChanged] = useManagedState<string>('');
  const [dateOfBirth, onDateOfBirthChanged] = useManagedState<Optional<string>>();
  const [gender, onGenderChanged] = useManagedState<Optional<string>>();
  const [email, onEmailChanged] = useManagedState<Optional<string>>();
  const [phoneType, onPhoneTypeChanged] = useManagedState<Optional<PhoneType>>(PhoneType.MOBILE);
  const [phone, onPhoneChanged] = useManagedState<Optional<string>>();
  const [address1, onAddress1Changed] = useManagedState<Optional<string>>();
  const [address2, onAddress2Changed] = useManagedState<Optional<string>>();
  const [city, onCityChanged] = useManagedState<Optional<string>>();
  const [state, onStateChanged] = useManagedState<Optional<string>>();
  const [zip, onZipChanged] = useManagedState<Optional<string>>();
  const [guardianRequest, onGuardianRequestChanged] = useManagedState<boolean>(false);
  const [guardianFirstName, onGuardianFirstNameChanged] = useManagedState<Optional<string>>();
  const [guardianLastName, onGuardianLastNameChanged] = useManagedState<Optional<string>>();
  const [guardianDateOfBirth, onGuardianDateOfBirthChanged] = useManagedState<Optional<string>>();

  const guestRegistrationRef = useRef<ValidatedComponent>(null);

  // OTP and captcha states
  const [otpId, setOtpId] = useState('');
  const [otpTtl, setOtpTtl] = useState(0);
  const [otp, setOtp] = useState('');
  const [otpEntryComplete, setOtpEntryComplete] = useState(false);
  const [otpTimerExpired, setOtpTimerExpired] = useState(false);
  const [otpDisabled, setOtpDisabled] = useState(false);
  const [enableOtpTimer, setEnableOtpTimer] = useState(true);
  const [showResend, setShowResend] = useState(false);
  const [disableResend, setDisableResend] = useState(false);

  const [otpError, setOtpError] = useState(false);
  const [otpErrorMessage, setOtpErrorMessage] = useState<string>('');
  const [otpProcessing, setOtpProcessing] = useState(false);
  const [captcha, setCaptcha] = useState<string>();
  const [validationErrors, setValidationErrors] = useState<Array<string>>([]);

  const [showOTPVerification, setShowOTPVerification] = useState(false);

  const onValidationChanged = (errors: Array<string>) => {
    setValidationErrors(errors);
  };

  useEffect(() => {
    if (otpError) {
      setOtp('');
    }
  }, [otpError]);

  useEffect(() => {
    if (otpEntryComplete) {
      validateOtpCode();
    }
  }, [otpEntryComplete]);

  const generateOtp = async () => {
    setOtpProcessing(true);
    try {
      const guestService = GuestService.create(Environment.apiBaseUrl as string);
      const response = await guestService.verifyEmail(email as string, captcha as string);
      setOtpId(response.id);
      setOtpTtl(response.ttl);
      setOtpError(false);
      setOtpEntryComplete(false);
      setOtpDisabled(false);
      setShowResend(false);
      setEnableOtpTimer(true);
      setOtpErrorMessage('');
      setCaptcha('');
    } catch (error) {
      setOtpErrorMessage(OTP_ERROR_MESSAGES.INVALID);
      setOtpError(true);
    }
    setOtpProcessing(false);
  };

  const onSubmit = async () => {
    if ((guestRegistrationRef.current?.isValid() ?? false) && captchaIsValid) {
      setShowOTPVerification(true);
      await generateOtp();
    }
  };

  const onRenewCaptcha = (value: string | null) => {
    setOtpTimerExpired(false);
    setEnableOtpTimer(false);
    setShowResend(true);
    setOtpError(false);
    setOtpProcessing(false);
    setOtpDisabled(true);
    setDisableResend(false);
    setCaptcha(value !== null ? value : undefined);
  };

  const onCaptchaChanged = (value: string | null) => {
    setCaptcha(value !== null ? value : undefined);
  };

  const onOtpChanged = (value: string) => {
    setOtp(value);
    setOtpErrorMessage('');
    setOtpError(false);
  };

  const onOtpComplete = async () => {
    setOtpEntryComplete(true);
  };

  const validateOtpCode = async () => {
    setOtpProcessing(true);
    try {
      await registerGuest({
        otpId,
        otpCode: otp,
        firstName: firstName,
        middleName,
        lastName: lastName,
        email: email,
        dateOfBirth: dateOfBirth,
        gender,
        phoneType,
        phone: phone,
        address1: address1,
        address2: address2,
        city: city,
        state: state,
        zip: zip,
        guardianRequest,
        guardianFirstName,
        guardianLastName,
        guardianDateOfBirth,
        transactionId: cart?.transactionId,
      } as GuestRegistrationRequest);
      setOtpError(false);
      navigation.dispatch(StackActions.replace(SCREEN_NAMES.CHECKOUT));
    } catch (error) {
      setOtpError(true);
      setOtp('');
      setOtpEntryComplete(false);
      //@ts-ignore
      switch (error.status) {
        case 429:
          setOtpErrorMessage(OTP_ERROR_MESSAGES.EXCEEDED);
          setOtpDisabled(true);
          setShowResend(false);
          break;
        case 401:
          setOtpErrorMessage(OTP_ERROR_MESSAGES.INVALID);
          break;
        default:
          setOtpErrorMessage(OTP_ERROR_MESSAGES.ERROR);
      }
    }
    setOtpProcessing(false);
  };

  const getOtpSubtitle = (): ReactNode => {
    return (
      <Text>
        A message with a verification code has been sent to {email}. Please do not navigate away from this page. Enter
        the code to continue.
      </Text>
    );
  };

  const resendVerificationCode = async () => {
    await generateOtp();
  };

  const onTimerComplete = () => {
    setOtpTimerExpired(true);
    setOtpDisabled(true);
    setEnableOtpTimer(false);
    setShowResend(true);
    setDisableResend(true);
  };

  const renderDialogActions = () => (
    <View
      testID={'guest-checkout-actions-container'}
      style={{
        flex: 1,
        flexDirection: deviceType === DeviceType.MOBILE ? 'column' : 'row',
        justifyContent: 'space-between',
        alignItems: deviceType === DeviceType.MOBILE ? 'center' : 'flex-end',
      }}
    >
      <View style={{ alignItems: deviceType === DeviceType.MOBILE ? 'center' : 'flex-start' }}>
        <ReCAPTCHA
          ref={captchaRef}
          style={{ alignItems: 'center' }}
          sitekey={Environment.captchaSiteKey as string}
          onChange={onCaptchaChanged}
        />
        {!captchaIsValid && <HelperText type="error">Please complete the Captcha to continue.</HelperText>}
      </View>
      <View
        style={{
          marginTop: deviceType === DeviceType.MOBILE ? 5 : 0,
          flexDirection: 'column',
          alignItems: deviceType === DeviceType.MOBILE ? 'center' : 'flex-end',
        }}
      >
        <Button testID={'guest-checkout-submit-btn'} onPress={onSubmit}>
          Submit
        </Button>
        <View>
          {validationErrors.length > 0 && (
            <HelperText type="error" visible={validationErrors.length > 0}>
              You are missing one or more required fields.
            </HelperText>
          )}
        </View>
      </View>
    </View>
  );

  const renderMinorRegistrationHeader = () => {
    if (isNonEmptyString(session?.appConfig.minorRegistrationHeader)) {
      return <HtmlView style={{ marginBottom: 10 }}>{session?.appConfig.minorRegistrationHeader}</HtmlView>;
    }
  };

  const renderGuardianSwitchLabel = () => {
    if (isNonEmptyString(session?.appConfig.guardianSwitchLabel)) {
      return <HtmlView style={{ marginLeft: 5 }}>{session?.appConfig.guardianSwitchLabel}</HtmlView>;
    }
  };

  const captchaIsValid = useMemo(() => {
    return isNonEmptyString(captcha);
  }, [captcha]);

  return (
    <>
      {!showOTPVerification ? (
        <ScrollView>
          <Dialog
            testID={'guest-checkout'}
            style={{ flex: 1, padding: 8, marginTop: 12, marginBottom: 12 }}
            actions={renderDialogActions()}
            actionsStyle={{ zIndex: -1 }}
          >
            <View style={{ flex: 1, alignItems: 'center', padding: 4 }}>
              <Header40>Guest Checkout</Header40>
            </View>
            <GuestRegistration
              ref={guestRegistrationRef}
              testID={'guest-checkout-registration'}
              firstName={firstName}
              onFirstNameChanged={onFirstNameChanged}
              middleName={middleName}
              onMiddleNameChanged={onMiddleNameChanged}
              lastName={lastName}
              onLastNameChanged={onLastNameChanged}
              dateOfBirth={dateOfBirth}
              onDateOfBirthChanged={onDateOfBirthChanged}
              email={email}
              onEmailChanged={onEmailChanged}
              genders={genders}
              gender={gender}
              onGenderChanged={onGenderChanged}
              phoneType={phoneType}
              onPhoneTypeChanged={onPhoneTypeChanged}
              phone={phone}
              onPhoneChanged={onPhoneChanged}
              address1={address1}
              onAddress1Changed={onAddress1Changed}
              address2={address2}
              onAddress2Changed={onAddress2Changed}
              city={city}
              onCityChanged={onCityChanged}
              state={state}
              onStateChanged={onStateChanged}
              zip={zip}
              onZipChanged={onZipChanged}
              signUpRelationshipEnabled={session?.appConfig.signupRelationshipEnabled}
              enableLast4={session?.appConfig.enableLast4Collection}
              onValidationChanged={onValidationChanged}
              minorRegistrationEnabled={session?.appConfig.minorSupportEnabled}
              minorRegistrationHeader={renderMinorRegistrationHeader()}
              minorAgeLimit={session?.appConfig.minorAgeExpiration}
              guardianSwitchLabel={renderGuardianSwitchLabel()}
              guardianRequest={guardianRequest}
              onGuardianRequestChanged={onGuardianRequestChanged}
              guardianFirstName={guardianFirstName}
              onGuardianFirstNameChanged={onGuardianFirstNameChanged}
              guardianLastName={guardianLastName}
              onGuardianLastNameChanged={onGuardianLastNameChanged}
              guardianDateOfBirth={guardianDateOfBirth}
              onGuardianDateOfBirthChanged={onGuardianDateOfBirthChanged}
            />
          </Dialog>
        </ScrollView>
      ) : (
        <Dialog style={{ alignItems: 'flex-start', marginTop: 50 }}>
          <OneTimePasswordVerification
            value={otp}
            error={otpError}
            errorMessage={otpErrorMessage}
            processing={otpProcessing}
            onCodeChanged={onOtpChanged}
            onCodeComplete={onOtpComplete}
            onTimerComplete={onTimerComplete}
            timerDuration={otpTtl}
            enableTimer={enableOtpTimer}
            disabled={otpDisabled}
            subtitle={getOtpSubtitle()}
            showResendVerificationCode={showResend}
            disableResendVerificationCode={disableResend}
            onResendVerificationCode={resendVerificationCode}
          />
          {otpTimerExpired && (
            <View testID={'guest-checkout-timer-expired-recapthca'} style={{ alignItems: 'center', zIndex: -1 }}>
              <ReCAPTCHA
                ref={captchaRef}
                style={{ alignItems: 'center' }}
                sitekey={Environment.captchaSiteKey as string}
                onChange={onRenewCaptcha}
              />
            </View>
          )}
        </Dialog>
      )}
    </>
  );
};
